/**
 * useSyllabusWorkspace — 강의 작업 화면의 상태(설계 FE-20260912 §4·§5).
 *
 * 쥐는 것:
 *   server   — 마지막으로 받은 강의(헤더 + 줄 + revision). 쓰기 응답으로 고쳐 나간다(workspaceModel.apply*).
 *   drafts   — 줄별 **조건 초안**(식). 줄을 옮겨 다니거나 서버를 다시 읽어도 버리지 않는다 — 다른 곳에서 지운 줄의 초안도
 *              남긴다(설계 §5 「초안을 자동 폐기하지 않는다」). 이름은 초안이 아니다 — 나무에서 이름을 치고 Enter 면 곧바로
 *              저장한다(오너 결정 2026-09-13: 손으로 목차를 치는 자리라 줄마다 버튼을 누르지 않게).
 *   results  — 줄별 마지막 검색. 실제로 보낸 식의 열쇠(key)와 응답을 함께 둔다 → 「재검색 필요」 판정의 기준.
 *   saving   — 줄별 조건 저장 상태(저장 중 · 실패 · 충돌).
 *
 * 쓰기(이름·줄 추가·순서·옮기기·지우기·조건 저장·헤더·공유)는 **강의 하나에 한 줄로 세워** 차례로 보낸다.
 * 앞 쓰기 응답의 revision 을 다음 쓰기의 if_revision 으로 싣는다. 409(stale)면 대기열을 멈추고, 초안은 그대로 둔 채
 * **대기열 안에서** 최신본을 다시 읽은 뒤 충돌을 알린다 — 그래야 「내 변경 다시 저장」이 옛 revision 으로 나가지 않는다.
 * 자동으로 덮어쓰지 않는다. 사용자가 「최신본 쓰기」나 「내 변경 다시 저장」을 고른다. 공유받은 강의는 원본 쓰기를 아예 보내지 않는다.
 *
 * 검색은 줄마다 세대 번호를 붙인다 — 늦게 온 응답은 그 줄의 최신 요청이 아니면 버린다. 같은 식·같은 쪽의 요청이
 * 진행 중이면 두 번 보내지 않고, 다른 식·쪽의 요청이 오면 앞 요청을 취소한다(서버는 끝까지 계산할 수 있다 — 화면이 버릴 뿐).
 * 줄을 고르면 지금 식(초안 포함)의 결과가 없을 때 한 번 찾고, 같은 줄에서 식이 바뀌면 작업 칸(WorkPanel)이 0.8초 뒤 한 번
 * 찾는다(설계 FE-20260913 §5 · 오너 결정 D4 「저절로 검색」). 펼칠 때·이름을 바꿀 때는 찾지 않는다.
 *
 * 늦은 응답의 기준은 강의 id 가 아니라 **작업 회차(epoch)** 다 — 7 → 8 → 7 로 돌아오면 id 는 같아도 판은 새것이다.
 *
 * 반환 API — 2026-09-13 검사 뒤 덧붙은 것(바뀐 것은 없다):
 *   - 충돌 뒤 최신본에 그 줄이 없으면 conflict = { nodeId:null, vanished:true, vanishedId, message } — 줄이 없어 줄 자리에
 *     띄울 수 없으니 강의 전체 안내(nodeId null)로 낸다. 초안은 queryOf(vanishedId) 로 남아 있다(자동 폐기하지 않는다).
 *     이때 resolveConflict('mine') 은 받지 않고(false, 안내 유지), 'theirs' 는 그 초안을 버리고, 인자 없음은 확인만 한다.
 *   - resolveConflict → Promise<boolean>(다시 저장이 됐는가) · search → Promise<boolean>(이 요청의 응답을 반영했는가).
 *   - 반환 함수는 렌더마다 새로 서지 않는다(effect 의존값에 넣어도 된다). resultOf·savingOf·isDirty·queryOf·freshnessOf 는
 *     그 상태가 바뀔 때만 바뀐다.
 *   - 초안은 setDraft 를 부른 그 순간부터 search·saveQuery·select 가 본다(같은 처리 안에서 「초안 반영 + 검색」이 된다).
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  getSyllabus, updateSyllabus, deleteSyllabus, copySyllabus, shareSyllabus,
  addNode, updateNode, deleteNode, reorderNodes, searchByExpr, describeSyllabusError,
} from './syllabusApi';
import { inspectExpr } from './syllabusQuery';
import {
  buildIndex, childrenOf, parentOf, descendantsOf, firstConditionId, pathNames, reorderIds, siblingsOf, orderWithInsertBefore,
} from './treeIndex';
import {
  exprKey, isQueryDirty, freshness, applyCreated, applyUpdated, applyDeleted, applyReordered, orderWithInsertAfter,
} from './workspaceModel';

export const PER_PAGE = 20;
const RESULT_CACHE = 20; // 줄별 결과는 최근 20줄만 — 오래 쓰면 본문 HTML 이 쌓인다
const lastRowKey = (sid) => `syllabus-last-row:${sid}`;

function readLastRow(sid) {
  try { return window.sessionStorage.getItem(lastRowKey(sid)); } catch (e) { return null; }
}
function writeLastRow(sid, id) {
  try {
    if (id == null) window.sessionStorage.removeItem(lastRowKey(sid));
    else window.sessionStorage.setItem(lastRowKey(sid), String(id));
  } catch (e) { /* 곁들이는 상태 */ }
}

const mapWith = (m, k, v) => { const n = new Map(m); if (v === undefined) n.delete(k); else n.set(k, v); return n; };
const mapWithout = (m, keys) => {
  if (!keys.some((k) => m.has(k))) return m;
  const n = new Map(m);
  keys.forEach((k) => n.delete(k));
  return n;
};
/** 최근에 쓴 줄을 맨 뒤로 — 넘치면 가장 오래 안 쓴 줄부터 뺀다(Map 은 넣은 순서를 기억하므로 지웠다 다시 넣는다) */
const mapTouch = (m, k, v) => {
  const n = new Map(m);
  n.delete(k);
  n.set(k, v);
  while (n.size > RESULT_CACHE) n.delete(n.keys().next().value);
  return n;
};

const nodeIn = (data, id) => (data?.items || []).find((it) => String(it.node_id) === String(id)) || null;

const CONFLICT_MSG = '다른 곳에서 강의가 바뀌어 최신본을 다시 읽었습니다. 다시 해 주세요.';
const CONFLICT_DRAFT_MSG = '다른 곳에서 강의가 바뀌었습니다. 내 초안은 그대로 두었습니다.';
const VANISHED_MSG = '이 행은 다른 곳에서 지워졌습니다. 기존 행으로는 다시 저장할 수 없습니다 — 초안은 남아 있습니다.';
/** 지워진 줄의 충돌 — 줄 자리가 없으니 강의 전체 안내(nodeId null)로 낸다. 초안 자리는 vanishedId */
const vanishedConflict = (k) => ({ nodeId: null, vanished: true, vanishedId: String(k), message: VANISHED_MSG });

function writeError(message, reason, httpStatus) {
  const e = new Error(message);
  e.reason = reason;
  if (httpStatus != null) e.httpStatus = httpStatus;
  return e;
}
const staleError = () => writeError('다른 곳에서 먼저 고쳤습니다. 새로 불러온 뒤 다시 해 주세요.', 'stale', 409);
// 서버도 403 read_only 로 막지만, 공유본에서는 원본 쓰기 요청 자체가 나가지 않아야 한다(설계 §7-10)
const readOnlyError = () => writeError('공유받은 강의는 고칠 수 없습니다. 사본을 만들어 고치세요.', 'read_only', 403);
const switchedError = () => writeError('강의가 바뀌어 요청을 보내지 않았습니다.', 'switched');

export default function useSyllabusWorkspace(syllabusId, { getAccessToken }) {
  const sid = syllabusId == null ? null : String(syllabusId);
  // 처음 그리는 순간부터 로딩 — effect 가 돌기 전 한 번 「빈 강의」로 번쩍이지 않게
  const [server, setServer] = useState(() => ({ loading: sid != null, data: null, error: null, errorObj: null }));
  const [drafts, setDrafts] = useState(() => new Map());
  const [results, setResults] = useState(() => new Map());
  const [saving, setSaving] = useState(() => new Map());
  const [conflict, setConflict] = useState(null); // { nodeId|null, message, vanished? }
  const [selectedId, setSelectedId] = useState(null);
  const [pendingWrites, setPendingWrites] = useState(0);
  const [copyRecovery, setCopyRecovery] = useState(null);
  const copyRecoveryRef = useRef(null);
  const draftBasesRef = useRef(new Map());
  const recoveryJobsRef = useRef(new Map());

  const sidRef = useRef(sid);
  const epochRef = useRef(0); // 강의를 열 때마다 +1 — 모든 비동기 응답은 자기 회차가 아니면 버린다
  const dataRef = useRef(null); // 서버 원본의 정본 — 대기열의 다음 쓰기가 곧바로 새 revision 을 읽어야 해서 ref 로 쥔다
  // 초안·결과의 정본은 ref 다 — setDraft 직후 같은 처리 안의 search·saveQuery 가 새 초안을 봐야 한다(렌더를 기다리지 않는다)
  const draftsRef = useRef(drafts);
  const resultsRef = useRef(results);
  const queueRef = useRef(Promise.resolve());
  const haltedRef = useRef(false);
  const loadGen = useRef(0);
  const searchSeq = useRef(0);
  const inflight = useRef(new Map()); // 줄 → { key, page, reqId, promise }
  const titleJobs = useRef(new Map()); // 제목 전환도 뒤이어 입력한 초안과 구분한다
  const editGenerations = useRef(new Map()); // 값이 원래대로 돌아와도 요청 뒤 편집 사실을 기억한다
  const saveJobs = useRef(new Map()); // 줄 → { key, ep, promise } — 같은 초안 저장을 두 번 보내지 않는다
  const copyJob = useRef(null); // { ep, promise } — 사본을 두 번 만들지 않는다
  const aliveRef = useRef(true);

  sidRef.current = sid;

  useEffect(() => {
    // StrictMode 는 effect 를 걷었다 다시 세운다 — 여기서 되살리지 않으면 개발 모드에서 모든 응답을 버린다(무한 로딩)
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const updateDrafts = useCallback((fn) => {
    const cur = draftsRef.current;
    const next = fn(cur);
    if (next === cur) return;
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const updateResults = useCallback((fn) => {
    const cur = resultsRef.current;
    const next = fn(cur);
    if (next === cur) return;
    resultsRef.current = next;
    setResults(next);
  }, []);

  const commitCopyRecovery = useCallback((next) => {
    copyRecoveryRef.current = next;
    if (aliveRef.current) setCopyRecovery(next);
  }, []);

  const commitData = useCallback((next) => {
    dataRef.current = next;
    setServer((s) => ({ ...s, data: next }));
  }, []);

  /** 이 줄들의 곁 상태를 모두 뗀다 — 진행 중 검색·저장도 잊어서 늦은 응답이 되살리지 않게 */
  const forget = useCallback((ids, { keepDrafts = false } = {}) => {
    const keys = ids.map(String);
    if (!keepDrafts) updateDrafts((m) => mapWithout(m, keys));
    updateResults((m) => mapWithout(m, keys));
    setSaving((m) => mapWithout(m, keys));
    keys.forEach((k) => { inflight.current.get(k)?.ctrl?.abort(); inflight.current.delete(k); saveJobs.current.delete(k); });
  }, [updateDrafts, updateResults]);

  // ── 읽기 ──

  /** 강의를 (다시) 읽는다. 초안은 남긴다 — 결과·저장 상태만 지워진 줄의 것을 뺀다 */
  const reload = useCallback(async () => {
    const at = sidRef.current;
    const ep = epochRef.current;
    if (at == null) return null;
    loadGen.current += 1;
    const g = loadGen.current;
    const mine = () => aliveRef.current && epochRef.current === ep && g === loadGen.current;
    setServer((s) => ({ ...s, loading: true, error: null, errorObj: null }));
    try {
      const d = await getSyllabus(at, getAccessToken);
      if (!mine()) return null;
      const cur = dataRef.current;
      // 쓰기 응답으로 이미 더 새 판을 쥐고 있으면(재조회가 쓰기보다 먼저 읽혔다) 옛 판으로 되돌리지 않는다 —
      // 되돌리면 다음 쓰기가 자기 자신 때문에 409 를 맞는다
      if (cur && Number.isFinite(cur.revision) && Number.isFinite(d?.revision) && d.revision < cur.revision) {
        setServer((s) => ({ ...s, loading: false }));
        return cur;
      }
      dataRef.current = d;
      setServer({ loading: false, data: d, error: null, errorObj: null });
      const alive = new Set((d?.items || []).map((it) => String(it.node_id)));
      const goneOf = (m) => [...m.keys()].filter((k) => !alive.has(k));
      // 사라진 줄의 진행 중 검색도 잊는다 — 늦은 응답이 결과를 되살리지 않게(remove 의 forget 과 같은 이유)
      goneOf(inflight.current).forEach((k) => { inflight.current.get(k)?.ctrl?.abort(); inflight.current.delete(k); });
      updateResults((m) => mapWithout(m, goneOf(m)));
      setSaving((m) => mapWithout(m, goneOf(m)));
      return d;
    } catch (e) {
      if (!mine()) return null;
      setServer((s) => ({ ...s, loading: false, error: describeSyllabusError(e), errorObj: e }));
      return null;
    }
  }, [getAccessToken, updateResults]);

  // ── 검색 ──

  /** 초안이 있으면 초안, 없으면 서버 식 — ref 에서 읽는다(방금 넣은 초안까지) */
  const draftOrServer = (k) => (draftsRef.current.has(k) ? draftsRef.current.get(k) : (nodeIn(dataRef.current, k)?.query ?? null));

  /**
   * 줄 하나를 지금 식(초안이 있으면 초안)으로 찾는다. 검색 규칙(구조·한도)으로 막히면 보내지 않는다 —
   * 모순은 서버가 그 묶음을 0건으로 계산하므로 보낸다(설계 §5).
   * 반환: 이 요청의 응답을 반영했으면 true(늦어서 버렸거나·막혔거나·실패면 false).
   */
  const search = useCallback((id, { page = 1 } = {}) => {
    const ep = epochRef.current;
    const k = String(id);
    const expr = draftOrServer(k);
    if (expr == null) return Promise.resolve(false);
    const insp = inspectExpr(expr, { mode: 'search' });
    if (!insp.ok) return Promise.resolve(false);
    const key = JSON.stringify(insp.normalized);
    const running = inflight.current.get(k);
    if (running && running.key === key && running.page === page) return running.promise; // 같은 요청을 두 번 쏘지 않는다
    // 다른 식·쪽의 요청이 가고 있으면 취소한다 — 늦은 응답은 어차피 버리므로(아래 mine) 기다릴 까닭이 없다
    if (running?.ctrl) running.ctrl.abort();
    searchSeq.current += 1;
    const ctrl = typeof AbortController === 'undefined' ? null : new AbortController();
    const job = {
      key, page, reqId: searchSeq.current, ctrl,
    };
    inflight.current.set(k, job);
    updateResults((m) => mapTouch(m, k, {
      ...(m.get(k) || {}), status: 'loading', pendingKey: key, pendingPage: page, error: null, errorPage: null, errorKey: null, errorReason: null, errorDetails: null,
    }));
    const mine = () => aliveRef.current && epochRef.current === ep && inflight.current.get(k) === job;
    job.promise = (async () => {
      try {
        const d = await searchByExpr({
          expr: insp.normalized, page, perPage: PER_PAGE, withBody: true, signal: ctrl?.signal ?? null,
        }, getAccessToken);
        if (!mine()) return false;
        inflight.current.delete(k);
        updateResults((m) => mapTouch(m, k, {
          status: 'done', key, page, data: d, error: null, errorReason: null, errorKey: null, errorPage: null, errorDetails: null,
          at: new Date().toISOString(), pendingKey: null, pendingPage: null,
        }));
        return true;
      } catch (e) {
        if (!mine()) return false;
        inflight.current.delete(k);
        // 실패해도 이전 성공 결과는 남긴다 — 이전 결과를 실패한 요청의 결과로 바꾸지 않는다
        updateResults((m) => mapTouch(m, k, {
          ...(m.get(k) || {}),
          status: 'error',
          error: describeSyllabusError(e),
          errorReason: e?.reason ?? (e?.httpStatus === 408 ? 'search_timeout' : null),
          errorKey: key,
          errorPage: page,
          errorDetails: e?.data ?? null,
          pendingKey: null,
          pendingPage: null,
        }));
        return false;
      }
    })();
    return job.promise;
  }, [getAccessToken, updateResults]); // eslint-disable-line react-hooks/exhaustive-deps -- draftOrServer 는 ref 만 읽는다

  // ── 선택 ──

  /**
   * 줄 고르기 — 지금 식(초안이 있으면 초안)의 결과가 아직 없으면 그 식으로 한 번 찾는다(설계 FE-20260913 §5 「행 고르기 → 즉시 1회」).
   * 조건이 없는 줄(제목 줄)·검색 규칙에 막히는 식·지금 식의 결과를 이미 받은 줄은 찾지 않는다. 식이 바뀐 옛 결과(stale)만 있으면 찾는다.
   */
  const select = useCallback((id, { auto = true } = {}) => {
    const at = sidRef.current;
    const k = id == null ? null : String(id);
    setSelectedId(k);
    if (at != null) writeLastRow(at, k);
    if (!auto || k == null) return;
    if (!nodeIn(dataRef.current, k)) return;
    const expr = draftOrServer(k);
    if (expr == null) return; // query:null 은 전체 검색이다
    const insp = inspectExpr(expr, { mode: 'search' });
    if (!insp.ok) return;
    const r = resultsRef.current.get(k);
    if (r?.data && r.key === JSON.stringify(insp.normalized)) return;
    search(k);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps -- draftOrServer 는 ref 만 읽는다

  // 강의가 바뀌면 모두 새로 — 그리고 마지막 줄(없으면 첫 조건 줄)을 고른다
  useEffect(() => {
    epochRef.current += 1;
    const ep = epochRef.current;
    updateDrafts(() => new Map());
    draftBasesRef.current = new Map();
    recoveryJobsRef.current = new Map([...recoveryJobsRef.current].filter(([key]) => key === 'copy'));
    updateResults(() => new Map());
    setSaving(new Map());
    setConflict(null);
    setSelectedId(null);
    setPendingWrites(0);
    inflight.current = new Map();
    saveJobs.current = new Map();
    titleJobs.current = new Map();
    editGenerations.current = new Map();
    // 사본 복구는 강의 전환으로 버리지 않는다. 사본 ID와 초안은 별도의 작업 상태다.
    haltedRef.current = false;
    queueRef.current = Promise.resolve();
    dataRef.current = null;
    if (sid == null) {
      loadGen.current += 1;
      setServer({ loading: false, data: null, error: null, errorObj: null });
      return;
    }
    setServer({ loading: true, data: null, error: null, errorObj: null });
    (async () => {
      const d = await reload();
      if (!d || epochRef.current !== ep) return;
      const idx = buildIndex(d.items || []);
      const last = readLastRow(sid);
      const start = last != null && idx.byId.has(last) ? last : firstConditionId(idx);
      if (start != null) select(start);
    })();
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps -- 강의가 바뀔 때만 새로 연다

  // ── 초안 ──

  const setDraft = useCallback((id, query) => {
    const k = String(id);
    editGenerations.current.set(k, (editGenerations.current.get(k) || 0) + 1);
    const node = nodeIn(dataRef.current, k);
    if (node && !draftsRef.current.has(k)) draftBasesRef.current.set(k, { ...node });
    // 원본과 같아지면 초안을 뗀다 — 「저장 필요」가 남지 않게.
    // 단 그 줄의 저장이 가고 있으면 떼지 않는다 — 원본이 곧 보낸 식으로 바뀌므로, 지금 원본과 같다고 떼면 저장이 끝난 뒤
    // 사용자의 마지막 입력 대신 보낸 식이 보인다(설계 §4 「저장 응답이 요청 이후의 새 입력을 지우면 안 된다」)
    const collapse = node && !saveJobs.current.has(k) && !titleJobs.current.has(k) && !isQueryDirty(query, node.query);
    updateDrafts((m) => (collapse ? mapWithout(m, [k]) : mapWith(m, k, query)));
  }, [updateDrafts]);

  const revertDraft = useCallback((id) => {
    const k = String(id);
    editGenerations.current.set(k, (editGenerations.current.get(k) || 0) + 1);
    // 저장 중 복귀도 사용자의 새 입력이다. 보낸 식이 응답으로 원본이 되어도 지금 보고 있는 원본값을 유지한다.
    const node = nodeIn(dataRef.current, k);
    updateDrafts((m) => ((saveJobs.current.has(k) || titleJobs.current.has(k)) && node
      ? mapWith(m, k, node.query ?? null) : mapWithout(m, [k])));
    // 되돌린 초안의 저장 실패 문구는 더 이상 맞지 않는다 — 저장 중이면 그 결과를 기다린다
    setSaving((m) => (m.get(k) && m.get(k).status !== 'saving' ? mapWithout(m, [k]) : m));
  }, [updateDrafts]);

  // ── 쓰기 대기열 ──

  /**
   * 쓰기 한 건을 줄 세운다. fn(revision, ctx) — ctx.at 은 강의 id, ctx.live() 는 「아직 같은 회차인가」.
   * 대기 수는 줄 선 순간부터 센다(앞 쓰기를 기다리는 것도 「저장 중」이다).
   */
  const enqueue = useCallback((fn) => {
    const ep = epochRef.current;
    const ctx = { at: sidRef.current, live: () => aliveRef.current && epochRef.current === ep };
    setPendingWrites((n) => n + 1);
    const run = async () => {
      if (!ctx.live()) throw switchedError();
      if (!dataRef.current) throw writeError('강의를 아직 불러오지 못했습니다.', 'not_loaded');
      if (haltedRef.current) throw staleError();
      if (dataRef.current.permission !== 'owner') throw readOnlyError();
      try {
        return await fn(dataRef.current.revision, ctx);
      } catch (e) {
        if (ctx.live() && (e?.httpStatus === 403 || e?.reason === 'read_only' || e?.reason === 'out_of_scope')) {
          // 소유권/접근권이 사라지면 뒤에 대기한 원본 쓰기도 보내지 않는다. 초안은 복사·보관할 수 있게 남긴다.
          await reload();
          if (ctx.live() && dataRef.current) commitData({ ...dataRef.current, permission: 'viewer' });
        }
        if (e?.reason === 'stale' && ctx.live()) {
          haltedRef.current = true;
          // 최신본을 대기열 안에서 읽는다 — 다 읽기 전에 뒤에 선 쓰기(내 변경 다시 저장 포함)가 옛 revision 으로 나가지 않게
          await reload();
          if (ctx.live()) setConflict((c) => c || { nodeId: null, message: CONFLICT_MSG });
        }
        throw e;
      }
    };
    const p = queueRef.current.then(run, run).finally(() => {
      if (ctx.live()) setPendingWrites((n) => Math.max(0, n - 1));
    });
    queueRef.current = p.catch(() => {});
    return p;
  }, [reload, commitData]);

  /** 쓰기 응답을 원본에 반영 — 대기열 안에서 곧바로(다음 쓰기가 새 revision 을 읽는다). 회차가 바뀌었으면 아무것도 안 한다 */
  const applyResponse = useCallback((ctx, res, patchItems) => {
    if (!ctx.live() || !dataRef.current) return false;
    const cur = dataRef.current;
    commitData({
      ...cur,
      revision: res?.revision ?? cur.revision,
      items: patchItems ? patchItems(cur.items || []) : cur.items,
    });
    return true;
  }, [commitData]);

  // ── 줄: 이름 · 추가 · 순서 · 옮기기 · 지우기 ──

  const rename = useCallback((id, name) => enqueue(async (rev, ctx) => {
    const res = await updateNode(ctx.at, Number(id), { name, ifRevision: rev }, getAccessToken);
    applyResponse(ctx, res, (items) => applyUpdated(items, res));
    return res;
  }), [enqueue, applyResponse, getAccessToken]);

  /** 새 줄을 afterId 다음으로 — 최신 형제 목록으로 순서를 만들어 N4 만 보낸다(N1 을 다시 보내지 않는다) */
  const placeAfter = useCallback((newId, afterId) => enqueue(async (rev, ctx) => {
    const idx = buildIndex(dataRef.current?.items || []);
    if (!idx.byId.has(String(newId))) return null;
    const pid = parentOf(idx, newId);
    const ids = orderWithInsertAfter(childrenOf(idx, pid), newId, afterId);
    if (!ids) return null;
    const res = await reorderNodes(ctx.at, pid == null ? null : Number(pid), ids.map(Number), { ifRevision: rev }, getAccessToken);
    applyResponse(ctx, res, (items) => applyReordered(items, ids));
    return res;
  }), [enqueue, applyResponse, getAccessToken]);

  /**
   * 줄 추가 — N1 은 형제 끝에 만든다. afterId 가 형제 중간이면 이어서 N4 로 그 다음에 옮긴다(설계 §W06).
   * 만들기는 됐는데 순서만 실패하면 { orderFailed } 로 알린다 — 다시 시도는 placeAfter(N4 만).
   */
  const create = useCallback(async ({ parentId = null, afterId = null, name, query = null } = {}) => {
    const ep = epochRef.current;
    const node = await enqueue(async (rev, ctx) => {
      const res = await addNode(ctx.at, {
        parentId: parentId == null ? null : Number(parentId), name, query: query ?? null, ifRevision: rev,
      }, getAccessToken);
      applyResponse(ctx, res, (items) => applyCreated(items, res));
      return res;
    });
    const newId = String(node.node_id);
    // 강의가 바뀌었으면 새 강의에 순서 쓰기를 섞지 않는다
    if (afterId == null || epochRef.current !== ep) return { nodeId: newId, orderFailed: false, error: null };
    try {
      await placeAfter(newId, afterId);
      return { nodeId: newId, orderFailed: false, error: null };
    } catch (e) {
      return { nodeId: newId, orderFailed: true, error: describeSyllabusError(e) };
    }
  }, [enqueue, applyResponse, placeAfter, getAccessToken]);

  const move = useCallback((id, dir) => enqueue(async (rev, ctx) => {
    const idx = buildIndex(dataRef.current?.items || []);
    if (!idx.byId.has(String(id))) return null;
    const ids = reorderIds(siblingsOf(idx, id), id, dir);
    if (!ids) return null;
    const pid = parentOf(idx, id);
    const res = await reorderNodes(ctx.at, pid == null ? null : Number(pid), ids.map(Number), { ifRevision: rev }, getAccessToken);
    applyResponse(ctx, res, (items) => applyReordered(items, ids));
    return res;
  }), [enqueue, applyResponse, getAccessToken]);

  const reparent = useCallback((id, parentId) => enqueue(async (rev, ctx) => {
    const res = await updateNode(ctx.at, Number(id), { parentId: parentId == null ? null : Number(parentId), ifRevision: rev }, getAccessToken);
    applyResponse(ctx, res, (items) => applyUpdated(items, res));
    return res;
  }), [enqueue, applyResponse, getAccessToken]);

  /** 형제 안에서 beforeId 앞으로(null 이면 끝) — 끌어 놓기의 순서 몫(N4). 최신 형제 목록으로 만들고, 이미 그 자리면 보내지 않는다 */
  const placeBefore = useCallback((id, beforeId) => enqueue(async (rev, ctx) => {
    const idx = buildIndex(dataRef.current?.items || []);
    if (!idx.byId.has(String(id))) return null;
    const pid = parentOf(idx, id);
    const cur = childrenOf(idx, pid);
    const ids = orderWithInsertBefore(cur, id, beforeId);
    if (ids.every((k, i) => k === cur[i])) return null;
    const res = await reorderNodes(ctx.at, pid == null ? null : Number(pid), ids.map(Number), { ifRevision: rev }, getAccessToken);
    applyResponse(ctx, res, (items) => applyReordered(items, ids));
    return res;
  }), [enqueue, applyResponse, getAccessToken]);

  /**
   * 끌어 놓기 — parentId 밑, beforeId 앞으로(beforeId 가 null 이면 끝).
   * 부모가 바뀌면 N2 가 먼저다 — 서버는 부모가 **실제로 바뀐** 줄만 새 형제의 끝에 둔다(api/v2/syllabus.py · 계약 「N2 부모 변경 시 형제 끝」).
   * 그래서 끝이면 N2 한 번으로 끝나고, 사이에 끼울 때만 이어서 N4 를 보낸다. 부모가 같으면 처음부터 N4 하나다.
   * 부모는 옮겨졌는데 순서만 실패하면 { orderFailed } — 줄 추가(create)와 같은 모양이다. 다시 시도는 같은 부름을 한 번 더:
   * 부모가 이미 맞으므로 N4 만 나간다. N4 하나뿐인 경우의 실패는 그대로 던진다(반쯤 된 것이 없다).
   */
  const moveTo = useCallback(async (id, { parentId = null, beforeId = null } = {}) => {
    const ep = epochRef.current;
    const k = String(id);
    const pk = parentId == null ? null : String(parentId);
    const idx = buildIndex(dataRef.current?.items || []);
    if (!idx.byId.has(k)) return { orderFailed: false, error: null };
    if (parentOf(idx, k) === pk) {
      await placeBefore(k, beforeId);
      return { orderFailed: false, error: null };
    }
    await reparent(k, pk);
    // 강의가 바뀌었으면 새 강의에 순서 쓰기를 섞지 않는다
    if (beforeId == null || epochRef.current !== ep) return { orderFailed: false, error: null };
    try {
      await placeBefore(k, beforeId);
      return { orderFailed: false, error: null };
    } catch (e) {
      return { orderFailed: true, error: describeSyllabusError(e) };
    }
  }, [placeBefore, reparent]);

  /** 지우기 — 하위까지. 그 줄들의 초안·결과·진행 중 검색을 떼고, 고른 줄이 지워졌으면 지운 줄의 부모를 고른다 */
  const remove = useCallback((id) => enqueue(async (rev, ctx) => {
    const k = String(id);
    const idx = buildIndex(dataRef.current?.items || []);
    const fallback = parentOf(idx, k);
    const res = await deleteNode(ctx.at, Number(k), { ifRevision: rev }, getAccessToken);
    if (!ctx.live()) return res;
    const gone = (res?.deleted_node_ids || [k, ...descendantsOf(idx, k)]).map(String);
    applyResponse(ctx, res, (items) => applyDeleted(items, gone));
    forget(gone);
    setSelectedId((cur) => {
      if (cur == null || !gone.includes(cur)) return cur;
      writeLastRow(ctx.at, fallback);
      return fallback;
    });
    return res;
  }), [enqueue, applyResponse, forget, getAccessToken]);

  // ── 조건 저장 · 제목 줄로 ──

  /**
   * 원본 저장 — 초안을 저장 규칙(구조·한도·모순)으로 검사해 정규화된 식을 보낸다.
   * 저장하는 동안 새로 친 입력은 지우지 않는다 — 보낸 초안과 지금 초안이 같을 때만 초안을 뗀다(§4).
   * 같은 초안의 저장이 진행 중이면 그 저장을 기다린다(두 번 보내지 않는다). 반환 Promise<boolean>.
   */
  const saveQuery = useCallback((id) => {
    const ep = epochRef.current;
    const k = String(id);
    const draft = draftsRef.current.get(k);
    if (draft === undefined) return Promise.resolve(false);
    const node = nodeIn(dataRef.current, k);
    // 앞 저장이 가고 있으면 지금 원본은 곧 낡는다 — 「원본과 같음」으로 끝내지 않고 줄 세워 보낸다
    if (node && !saveJobs.current.has(k) && !isQueryDirty(draft, node.query)) {
      // 원본과 같은 초안 — 보낼 것이 없다
      updateDrafts((m) => mapWithout(m, [k]));
      setSaving((m) => mapWithout(m, [k]));
      return Promise.resolve(true);
    }
    const insp = inspectExpr(draft, { mode: 'store' });
    if (!insp.ok) {
      setSaving((m) => mapWith(m, k, { status: 'error', message: insp.error.message }));
      return Promise.resolve(false);
    }
    const sentKey = exprKey(draft);
    const running = saveJobs.current.get(k);
    if (running && running.key === sentKey && running.ep === ep) return running.promise;
    const job = { key: sentKey, ep };
    saveJobs.current.set(k, job);
    const latest = () => epochRef.current === ep && saveJobs.current.get(k) === job;
    setSaving((m) => mapWith(m, k, { status: 'saving' }));
    job.promise = (async () => {
      try {
        await enqueue(async (rev, ctx) => {
          const res = await updateNode(ctx.at, Number(k), { query: insp.normalized, ifRevision: rev }, getAccessToken);
          applyResponse(ctx, res, (items) => applyUpdated(items, res));
          return res;
        });
        if (epochRef.current !== ep) return true;
        updateDrafts((m) => (m.has(k) && exprKey(m.get(k)) === sentKey ? mapWithout(m, [k]) : m));
        // 남은 입력의 편집 기준은 방금 저장에 성공한 원본이다. 다음 충돌 비교에 이전 저장 전 원본을 쓰지 않는다.
        const savedBase = nodeIn(dataRef.current, k);
        if (draftsRef.current.has(k) && savedBase) draftBasesRef.current.set(k, { ...savedBase });
        else draftBasesRef.current.delete(k);
        // 뒤이어 다른 초안의 저장이 줄 서 있으면 「저장 중」을 그쪽이 끝낼 때까지 둔다
        if (latest()) setSaving((m) => mapWithout(m, [k]));
        return true;
      } catch (e) {
        if (epochRef.current !== ep) return false;
        if (e?.reason === 'stale') {
          if (!nodeIn(dataRef.current, k)) {
            // 다시 읽은 최신본에 그 줄이 없다 — 줄 자리가 없으니 강의 전체 안내로(초안은 남긴다)
            setSaving((m) => mapWithout(m, [k]));
            setConflict(vanishedConflict(k));
          } else {
            setSaving((m) => mapWith(m, k, { status: 'conflict', message: describeSyllabusError(e) }));
            setConflict({ nodeId: k, message: CONFLICT_DRAFT_MSG, baseQuery: draftBasesRef.current.get(k)?.query ?? null });
          }
        } else if (latest()) {
          setSaving((m) => mapWith(m, k, { status: 'error', message: describeSyllabusError(e), details: e?.data ?? null, key: sentKey }));
        }
        return false;
      } finally {
        if (saveJobs.current.get(k) === job) saveJobs.current.delete(k);
      }
    })();
    return job.promise;
  }, [enqueue, applyResponse, updateDrafts, getAccessToken]);

  /** 제목 전환은 누른 시점의 조건을 뗀다. 요청 뒤 새로 편집/검색한 초안과 결과는 그대로 남긴다. */
  const makeTitleRow = useCallback((id) => {
    const k = String(id);
    const running = titleJobs.current.get(k);
    if (running) return running.promise;
    const generation = editGenerations.current.get(k) || 0;
    const job = { generation };
    titleJobs.current.set(k, job);
    job.promise = enqueue(async (rev, ctx) => {
      const res = await updateNode(ctx.at, Number(k), { query: null, ifRevision: rev }, getAccessToken);
      if (!applyResponse(ctx, res, (items) => applyUpdated(items, res))) return res;
      if ((editGenerations.current.get(k) || 0) === generation) {
        forget([k]);
        draftBasesRef.current.delete(k);
      } else {
        // setDraft/revertDraft는 전환 중 원본과 같은 값도 남긴다. 성공한 제목 원본과 비교해 이후 저장할 수 있다.
        const base = nodeIn(dataRef.current, k);
        if (base) draftBasesRef.current.set(k, { ...base });
      }
      return res;
    }).finally(() => { if (titleJobs.current.get(k) === job) titleJobs.current.delete(k); });
    return job.promise;
  }, [enqueue, applyResponse, forget, getAccessToken]);

  /**
   * 공통 조건 반영 — 계획의 행({ id, next })을 한 행씩 저장한다(설계 FE-20260913 §3 · 오너 결정 D1 「각각 항목에 저장」).
   * 여러 행을 한 번에 쓰는 서버 API 가 없어 쓰기 대기열 · revision 사슬을 그대로 쓴다.
   *   - 400 · 네트워크 오류는 그 행만 실패로 두고 다음 행으로(부분 실패를 개수로 남긴다)
   *   - 409(다른 곳에서 먼저 고침)면 대기열이 멈추므로 남은 행은 보내지 않는다(halted) — 최신본을 확인한 뒤 다시 반영한다
   *   - 그사이 저장 전 변경이 생긴 행은 건너뛴다 — 원본을 바꾸면 나중에 그 초안을 저장할 때 공통 조건을 덮는다
   *   - shouldStop() 이 참이면 다음 행부터 보내지 않는다([멈춤]) · onStep(id, 'saving'|'done'|'failed'|'halted'|'skipped')
   * 반환 { done:[id], failed:[{ id, message }], left:[id](보내지 않은 행), halted }
   */
  const applyCommonRows = useCallback((rows, { onStep = () => {}, shouldStop = () => false } = {}) => {
    const ep = epochRef.current;
    const list = Array.isArray(rows) ? rows : [];
    const out = {
      done: [], failed: [], left: [], halted: false,
    };
    const step = async (i) => {
      if (i >= list.length) return out;
      const k = String(list[i].id);
      if (epochRef.current !== ep || out.halted || shouldStop()) {
        out.left.push(k);
        return step(i + 1);
      }
      const node = nodeIn(dataRef.current, k);
      if (!node) {
        out.failed.push({ id: k, message: '이 행은 다른 곳에서 지워졌습니다.' });
        onStep(k, 'failed');
        return step(i + 1);
      }
      if (draftsRef.current.has(k)) {
        if (isQueryDirty(draftsRef.current.get(k), node.query)) {
          out.left.push(k);
          onStep(k, 'skipped');
          return step(i + 1);
        }
        // 원본과 같은 초안은 잃을 것이 없다 — 떼지 않으면 원본이 바뀐 순간 「저장 전 변경」으로 둔갑한다
        updateDrafts((m) => mapWithout(m, [k]));
      }
      onStep(k, 'saving');
      try {
        await enqueue(async (rev, ctx) => {
          const res = await updateNode(ctx.at, Number(k), { query: list[i].next, ifRevision: rev }, getAccessToken);
          applyResponse(ctx, res, (items) => applyUpdated(items, res));
          return res;
        });
        out.done.push(k);
        onStep(k, 'done');
      } catch (e) {
        if (e?.reason === 'stale' || e?.httpStatus === 409) {
          out.halted = true;
          out.left.push(k);
          onStep(k, 'halted');
        } else {
          out.failed.push({ id: k, message: describeSyllabusError(e) });
          onStep(k, 'failed');
        }
      }
      return step(i + 1);
    };
    return step(0);
  }, [enqueue, applyResponse, updateDrafts, getAccessToken]);

  /**
   * 충돌 풀기 — 'theirs' 최신본 쓰기(내 초안 버림) · 'mine' 내 변경 다시 저장(새 revision 으로) · 그 밖은 확인만(초안 둠).
   * 최신본에 그 줄이 없으면 옛 id 로 다시 보내지 않는다 — 초안은 남기고 vanished 충돌로 다시 알린다.
   */
  const resolveConflict = useCallback(async (how) => {
    const c = conflict;
    if (c?.vanished) {
      // 줄이 없어 옛 id 로 다시 보낼 수 없다 — 「다시 저장」은 받지 않고 안내를 그대로 둔다(대기열도 멈춘 채)
      if (how === 'mine') return false;
      haltedRef.current = false;
      setConflict(null);
      if (how === 'theirs') {
        updateDrafts((m) => mapWithout(m, [c.vanishedId]));
        setSaving((m) => mapWithout(m, [c.vanishedId]));
      }
      return false;
    }
    haltedRef.current = false;
    setConflict(null);
    if (!c?.nodeId) return false;
    const k = c.nodeId;
    if (how === 'theirs') {
      updateDrafts((m) => mapWithout(m, [k]));
      setSaving((m) => mapWithout(m, [k]));
      return false;
    }
    if (how === 'mine') {
      if (!nodeIn(dataRef.current, k)) {
        haltedRef.current = true;
        setSaving((m) => mapWithout(m, [k]));
        setConflict(vanishedConflict(k));
        return false;
      }
      return saveQuery(k);
    }
    return false;
  }, [conflict, saveQuery, updateDrafts]);

  // ── 강의 헤더 ──

  const applyHeader = useCallback((ctx, res) => {
    if (ctx.live() && dataRef.current) commitData({ ...dataRef.current, ...res, items: dataRef.current.items });
  }, [commitData]);

  const updateHeader = useCallback((patch) => enqueue(async (rev, ctx) => {
    const res = await updateSyllabus(ctx.at, { ...patch, ifRevision: rev }, getAccessToken);
    applyHeader(ctx, res); // P4 는 헤더(revision 포함)를 돌려준다 — kyhub api/v2/syllabus.py patch → _header(syl)
    return res;
  }), [enqueue, applyHeader, getAccessToken]);

  const share = useCallback((scope) => enqueue(async (rev, ctx) => {
    const res = await shareSyllabus(ctx.at, scope, { ifRevision: rev }, getAccessToken);
    applyHeader(ctx, res);
    return res;
  }), [enqueue, applyHeader, getAccessToken]);

  // P5 는 if_revision 을 받지 않는다 — 대기열에만 세워 앞 쓰기가 끝난 뒤 지운다
  const removeSyllabus = useCallback(() => enqueue(async (_rev, ctx) => {
    const res = await deleteSyllabus(ctx.at, getAccessToken);
    if (ctx.live()) {
      updateDrafts(() => new Map());
      draftBasesRef.current.clear();
      commitCopyRecovery(null);
      setConflict(null);
    }
    return res;
  }), [enqueue, getAccessToken, updateDrafts, commitCopyRecovery]);

  /** 사본 복구는 메뉴의 수명이 아니라 작업 세션에 둔다. P6 이후에는 이 상태를 통해 P3/N2만 이어간다. */
  const retryCopyRecovery = useCallback(({ targetId } = {}) => {
    const existing = copyRecoveryRef.current;
    if (!existing?.copyId) return Promise.resolve(null);
    if (recoveryJobsRef.current.has('copy')) return recoveryJobsRef.current.get('copy');
    const selected = targetId == null ? existing.targetId : String(targetId);
    const job = { ...existing, targetId: selected, status: 'loading', error: null };
    commitCopyRecovery(job);
    const promise = (async () => {
      try {
        const d = await getSyllabus(job.copyId, getAccessToken);
        if (copyRecoveryRef.current !== job) return null;
        if (d.permission !== 'owner') throw readOnlyError();
        const idx = buildIndex(d.items || []);
        job.candidates = [...idx.byId.keys()].map((id) => ({ nodeId: id, path: pathNames(idx, id).join(' › ') }));
        const hits = [...idx.byId.keys()].filter((id) => JSON.stringify(pathNames(idx, id)) === job.sourcePath);
        const target = selected ?? (hits.length === 1 ? hits[0] : null);
        if (target == null || !idx.byId.has(String(target))) {
          job.targetId = null;
          job.status = 'choose';
          job.error = '사본에서 같은 자리의 행을 하나로 찾지 못했습니다. 조건을 반영할 행을 선택하세요.';
          commitCopyRecovery({ ...job });
          return { copyId: job.copyId, applied: false, reason: job.error };
        }
        job.targetId = String(target);
        const insp = job.draft == null ? { ok: true, normalized: null } : inspectExpr(job.draft, { mode: 'store' });
        if (!insp.ok) throw writeError(insp.error.message, insp.error.reason);
        if (!aliveRef.current) return { copyId: job.copyId, applied: false, reason: '작업 화면을 떠나 조건 반영을 중단했습니다.' };
        job.status = 'saving';
        // 같은 객체를 유지해 응답의 작업 정체성을 확인한다.
        commitCopyRecovery(job);
        let res;
        if (sidRef.current === job.copyId && dataRef.current) {
          // 사본을 현재 작업 화면에서 연 경우, 다른 이름/조건 쓰기와 같은 대기열을 이용한다.
          if (d.revision >= dataRef.current.revision) commitData(d);
          res = await enqueue(async (rev, ctx) => {
            const saved = await updateNode(ctx.at, Number(target), { query: insp.normalized, ifRevision: rev }, getAccessToken);
            const updated = { ...idx.byId.get(String(target)), ...saved, query: saved?.query ?? insp.normalized };
            applyResponse(ctx, saved, (items) => applyUpdated(items, updated));
            return saved;
          });
        } else {
          res = await updateNode(job.copyId, Number(target), { query: insp.normalized, ifRevision: d.revision }, getAccessToken);
        }
        if (copyRecoveryRef.current !== job) return null;
        if (aliveRef.current && sidRef.current === job.copyId && dataRef.current && (res?.revision ?? d.revision) >= dataRef.current.revision) {
          const updated = { ...idx.byId.get(String(target)), ...res, query: res?.query ?? insp.normalized };
          commitData({ ...dataRef.current, revision: res?.revision ?? d.revision, items: applyUpdated(dataRef.current.items, updated) });
        }
        commitCopyRecovery(null);
        return { copyId: job.copyId, applied: true, reason: null };
      } catch (e) {
        if (copyRecoveryRef.current !== job) return null;
        job.status = 'error';
        job.error = describeSyllabusError(e);
        commitCopyRecovery({ ...job });
        return { copyId: job.copyId, applied: false, reason: job.error };
      } finally {
        if (recoveryJobsRef.current.get('copy') === promise) recoveryJobsRef.current.delete('copy');
      }
    })();
    recoveryJobsRef.current.set('copy', promise);
    return promise;
  }, [getAccessToken, commitCopyRecovery, commitData, enqueue, applyResponse]);

  const setCopyRecoveryDraft = useCallback((query) => {
    const r = copyRecoveryRef.current;
    if (!r || ['creating', 'loading', 'saving'].includes(r.status)) return;
    commitCopyRecovery({ ...r, draft: query, error: null });
  }, [commitCopyRecovery]);

  const discardCopyRecovery = useCallback(() => {
    const r = copyRecoveryRef.current;
    if (r && ['creating', 'loading', 'saving'].includes(r.status)) return false;
    commitCopyRecovery(null);
    return true;
  }, [commitCopyRecovery]);

  const copy = useCallback(({ withDraftOf = null } = {}) => {
    if (copyJob.current) return copyJob.current.promise;
    // 메뉴를 닫았다 열어도 이미 만든 사본을 다시 만들지 않는다.
    if (copyRecoveryRef.current?.copyId) {
      const r = copyRecoveryRef.current;
      return Promise.resolve({ copyId: r.copyId, applied: false, reason: r.error || '진행 중인 사본 조건 반영을 먼저 완료하세요.' });
    }
    const at = sidRef.current;
    const k = withDraftOf == null ? null : String(withDraftOf);
    const draft = k != null && draftsRef.current.has(k) ? JSON.parse(JSON.stringify(draftsRef.current.get(k))) : undefined;
    const idx = buildIndex(dataRef.current?.items || []);
    const record = draft === undefined ? null : {
      sourceId: at, sourceNodeId: k, sourceName: nodeIn(dataRef.current, k)?.name || '조건 초안',
      sourcePath: JSON.stringify(pathNames(idx, k)), draft, copyId: null,
      candidates: [], targetId: null, status: 'creating', error: null,
    };
    if (record) commitCopyRecovery(record);
    const job = {};
    job.promise = (async () => {
      try {
        const header = await copySyllabus(at, {}, getAccessToken);
        const copyId = String(header.syllabus_id);
        if (!record) return { copyId, applied: false, reason: null };
        if (!aliveRef.current || copyRecoveryRef.current !== record) return { copyId, applied: false, reason: '작업 화면을 떠나 조건 반영을 중단했습니다.' };
        commitCopyRecovery({ ...record, copyId, status: 'pending' });
        return await retryCopyRecovery();
      } catch (e) {
        // P6 자체가 실패했을 때만 처음부터 만들 수 있다. P3/N2 실패는 복구 상태로 반환한다.
        if (copyRecoveryRef.current?.copyId == null) commitCopyRecovery(null);
        throw e;
      } finally {
        if (copyJob.current === job) copyJob.current = null;
      }
    })();
    copyJob.current = job;
    return job.promise;
  }, [getAccessToken, commitCopyRecovery, retryCopyRecovery]);

  /** 사라진 행의 초안을 새 위치에 보관. 기존 ID로 PATCH하지 않고 최신 revision으로 N1을 딱 한 번 보낸다. */
  const recoverOrphanDraft = useCallback((id, { name, parentId = null } = {}) => {
    const k = String(id);
    if (recoveryJobsRef.current.has(k)) return recoveryJobsRef.current.get(k);
    const draft = draftsRef.current.get(k);
    const sentKey = exprKey(draft);
    const trimmed = String(name || '').trim();
    const insp = draft === undefined ? null : (draft == null ? { ok: true, normalized: null } : inspectExpr(draft, { mode: 'store' }));
    if (!trimmed || trimmed.length > 200 || !insp?.ok || nodeIn(dataRef.current, k)) {
      return Promise.reject(writeError(insp?.error?.message || '보관할 초안과 새 행 이름을 확인하세요.', 'invalid_recovery'));
    }
    const ep = epochRef.current;
    const promise = (async () => {
      const d = await reload();
      if (!d || ep !== epochRef.current) throw switchedError();
      if (parentId != null && !nodeIn(d, parentId)) throw writeError('선택한 상위 행이 없어졌습니다. 위치를 다시 선택하세요.', 'missing_parent');
      // 사용자가 복구를 확정했으므로 삭제 충돌의 중지를 해제한다. 다른 행 충돌을 묵시적으로 해제하지 않는다.
      if (conflict?.vanished && conflict.vanishedId === k) { haltedRef.current = false; setConflict(null); }
      const created = await create({ name: trimmed, parentId, query: insp.normalized });
      if (ep !== epochRef.current) return created;
      updateDrafts((m) => (m.has(k) && exprKey(m.get(k)) === sentKey ? mapWithout(m, [k]) : m));
      if (!draftsRef.current.has(k)) draftBasesRef.current.delete(k);
      select(created.nodeId, { auto: false });
      return created;
    })().finally(() => { if (recoveryJobsRef.current.get(k) === promise) recoveryJobsRef.current.delete(k); });
    recoveryJobsRef.current.set(k, promise);
    return promise;
  }, [reload, create, select, conflict, updateDrafts]);

  const discardOrphanDraft = useCallback((id) => {
    const k = String(id);
    if (nodeIn(dataRef.current, k) || recoveryJobsRef.current.has(k)) return false;
    updateDrafts((m) => mapWithout(m, [k]));
    draftBasesRef.current.delete(k);
    if (conflict?.vanishedId === k) { haltedRef.current = false; setConflict(null); }
    return true;
  }, [conflict, updateDrafts]);

  // ── 파생 ──

  const data = server.data && sid != null && String(server.data.syllabus_id) === sid ? server.data : null;
  const items = useMemo(() => data?.items || [], [data]);
  const index = useMemo(() => buildIndex(items), [items]);
  const owner = data?.permission === 'owner';

  const orphanDrafts = useMemo(() => [...drafts.entries()].filter(([k]) => !index.byId.has(k)).map(([nodeId, query]) => ({
    nodeId, query, name: draftBasesRef.current.get(nodeId)?.name || '복구한 조건행',
    parentId: draftBasesRef.current.get(nodeId)?.parent_id ?? null,
  })), [drafts, index]);

  const dirtyIds = useMemo(() => {
    const out = new Set();
    drafts.forEach((q, k) => { const n = index.byId.get(k); if (n && isQueryDirty(q, n.query)) out.add(k); });
    return out;
  }, [drafts, index]);

  const queryOf = useCallback((id) => {
    const k = String(id);
    return drafts.has(k) ? drafts.get(k) : (index.byId.get(k)?.query ?? null);
  }, [drafts, index]);

  const freshnessOf = useCallback((id) => {
    const k = String(id);
    const q = queryOf(k);
    const insp = q == null ? null : inspectExpr(q, { mode: 'search' });
    const key = insp?.ok ? JSON.stringify(insp.normalized) : null;
    return freshness(results.get(k), key);
  }, [queryOf, results]);

  // 진행 중 요청과 다른 입력은 현재 서버와 우연히 같아도 이탈 시 잃을 수 있는 초안이다.
  const pendingDrafts = [...drafts].some(([k, q]) => {
    const save = saveJobs.current.get(k);
    const title = titleJobs.current.get(k);
    return (save && exprKey(q) !== save.key) || (title && (editGenerations.current.get(k) || 0) !== title.generation);
  });

  const isDirty = useCallback((id) => dirtyIds.has(String(id)), [dirtyIds]);
  const resultOf = useCallback((id) => results.get(String(id)) || null, [results]);
  const savingOf = useCallback((id) => saving.get(String(id)) || null, [saving]);

  return {
    loading: server.loading,
    error: server.error,
    errorObj: server.errorObj || null,
    data,
    items,
    index,
    owner,
    readOnly: !owner,
    reload,
    selectedId: selectedId != null && index.byId.has(selectedId) ? selectedId : null,
    select,
    queryOf,
    dirtyIds,
    isDirty,
    hasUnsaved: dirtyIds.size > 0 || pendingDrafts || orphanDrafts.length > 0 || copyRecovery != null,
    orphanDrafts, recoverOrphanDraft, discardOrphanDraft,
    copyRecovery, retryCopyRecovery, discardCopyRecovery, setCopyRecoveryDraft,
    setDraft,
    revertDraft,
    resultOf,
    freshnessOf,
    search,
    savingOf,
    conflict,
    resolveConflict,
    pendingWrites,
    rename,
    create,
    placeAfter,
    move,
    reparent,
    placeBefore,
    moveTo,
    remove,
    saveQuery,
    makeTitleRow,
    applyCommonRows,
    updateHeader,
    share,
    copy,
    removeSyllabus,
  };
}
